import { isIOSWebViewGold as detectIOSWebViewGold } from './webviewgold-detection';
import { getCachedToken } from './token-cache';
import { waitForToken, getTokenFromCache } from './token-prefetch';
import { logPush } from './push-diagnostics';

export function isWebViewGold(): boolean {
  if (typeof navigator === 'undefined') return false;

  const hasUserAgent = navigator.userAgent.includes('WebViewGold');
  const hasInjectedVars = !!(
    window.uuid ||
    window.firebaseplayerid !== undefined ||
    window.versionNumber ||
    window.bundleNumber
  );

  return hasUserAgent || hasInjectedVars;
}

export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isIOSWebViewGold(): boolean {
  return detectIOSWebViewGold();
}

export function isSafariIOS(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (!isIOS) return false;

  // Check if it's Safari (not Chrome, Firefox, or other browsers on iOS)
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);

  return isSafari;
}

export function supportsServiceWorker(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/**
 * Determines if subscription checks should be bypassed
 * Returns true for:
 * - WebViewGold iOS app (Apple App Store version)
 * - Safari on iOS (iPhone)
 * - Safari on iPad
 *
 * Returns false for:
 * - Other browsers on iOS (Chrome, Firefox, etc.)
 * - Android devices
 * - Desktop browsers
 *
 * This is for Apple App Store compliance - the native iOS app and Safari iOS/iPad
 * users get free access while other platforms maintain subscription logic
 */
export function shouldBypassSubscription(): boolean {
  return isIOSWebViewGold() || isSafariIOS();
}

/**
 * Retrieves the native FCM token from WebViewGold iOS app
 * Uses the new cache-first prefetch system for reliable token retrieval
 * Returns null if not in WebViewGold iOS or if token is not available
 */
export async function getWebViewGoldIOSToken(): Promise<string | null> {
  if (!isIOSWebViewGold()) {
    logPush('info', 'prefetch', 'Not running in WebViewGold iOS - skipping token retrieval');
    return null;
  }

  logPush('info', 'prefetch', 'Retrieving FCM token for iOS WebViewGold');

  const cachedResult = getCachedToken();
  if (cachedResult.token) {
    logPush('success', 'cache', `Using cached token (source: ${cachedResult.source})`);
    return cachedResult.token;
  }

  const prefetchedToken = getTokenFromCache();
  if (prefetchedToken) {
    logPush('success', 'prefetch', 'Using prefetched token from memory');
    return prefetchedToken;
  }

  logPush('info', 'prefetch', 'No cached token, waiting for prefetch to complete');
  const token = await waitForToken(15000);

  if (token) {
    logPush('success', 'prefetch', `Token retrieved successfully (length: ${token.length})`);
    return token;
  } else {
    logPush('error', 'prefetch', 'Failed to retrieve FCM token after waiting');
    return null;
  }
}
